import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ConnectionStatus } from '@capacitor/network';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { BoondockFacade, BoondockService } from '@curbnturf/boondock-listing';
import { OfflineBoondockService } from '@curbnturf/boondock-listing/src/lib/offline-boondock.service';
import { MapIconsComponent } from '@curbnturf/custom-svgs/src/lib/map-icons/map-icons.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import {
  Boondock,
  DEFAULT_IMAGE_URL,
  FlowType,
  HelperFunctions,
  IBoondock,
  IPhoto,
  IUser,
  PhotoSizes,
} from '@curbnturf/entities';
import { CheckedComponent } from '@curbnturf/form-helpers/src/lib/components/checked/checked.component';
import { boondockComplete } from '@curbnturf/helpers';
import { Logger } from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import { HideConfirmationModalComponent } from '@curbnturf/site-components/src/lib/hide-confirmation-modal/hide-confirmation-modal.component';
import { StatusFacade } from '@curbnturf/status';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController } from '@ionic/angular';
import { Subscription } from 'rxjs';

const { boondockCompletePercentage, findBoondockLatestStep, findBoondockSteps } = HelperFunctions;

@Component({
  selector: 'curbnturf-boondock-list-item',
  templateUrl: './boondock-list-item.component.html',
  styleUrls: ['./boondock-list-item.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    DeleteConfirmationModalComponent,
    HideConfirmationModalComponent,
    IonicModule,
    NgIf,
    MapIconsComponent,
    MiscSvgComponent,
    NgFor,
    RouterLink,
  ],
})
export class BoondockListItemComponent implements OnInit, OnDestroy, OnChanges {
  @Input() boondock: Boondock;

  @Output() boondockRemoved: EventEmitter<string> = new EventEmitter<string>();

  public photoUrl: string = DEFAULT_IMAGE_URL;

  public completePercent: number = 0;
  public incompleteMessages: string[] = [];

  user: IUser;

  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'wifi' };

  subscriptions: Subscription[] = [];

  boondockRoute = '';

  getPhotoUrl = HelperFunctions.getPhotoUrlSquareMedium;

  get publishable(): boolean {
    return Boolean(!this.isAvailable && this.boondock.name && this.isComplete);
  }

  get notPublishable(): boolean {
    return Boolean(!this.isAvailable && (!this.boondock.name || !this.isComplete));
  }

  get isComplete(): boolean {
    return this.completePercent > 98;
  }

  get isAvailable(): boolean {
    return Boolean(this.boondock.available);
  }

  get isOnline() {
    return this.connectionStatus.connected;
  }

  constructor(
    private alertFacade: AlertFacade,
    private boondockFacade: BoondockFacade,
    private boondockService: BoondockService,
    private cd: ChangeDetectorRef,
    private loadingController: LoadingController,
    private logger: Logger,
    private offlineBoondockService: OfflineBoondockService,
    private photoService: PhotoService,
    private router: Router,
    private signUpFacade: SignUpStateFacade,
    private statusFacade: StatusFacade,
    private userFacade: UserFacade,
  ) {}

  async ngOnInit() {
    if (this.photoUrl === DEFAULT_IMAGE_URL) {
      // Don't reload the image every time this component is viewed.
      await this.getPhotoUrlFromKey(this.boondock?.photos?.[0]);
    }
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.connectionStatus = status;
      }),
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;
      }),
    );

    if (this.boondock) {
      this.makeBoondockViewLink();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.boondock?.currentValue) {
      this.updateCompletionStatus(changes.boondock.currentValue);
      this.makeBoondockViewLink();
    }
  }

  async getPhotoUrlFromKey(photo?: IPhoto): Promise<void> {
    if (photo) {
      if (this.boondock.id) {
        this.photoUrl = await this.photoService.getSavedPhotoUrl(photo, PhotoSizes.FlatSmall);
      } else if (this.boondock.syncId) {
        this.photoUrl = await this.photoService.getPhotoOffline(photo);
      }
    } else {
      this.photoUrl = DEFAULT_IMAGE_URL;
    }

    this.cd.detectChanges();
  }

  hide(boondock: IBoondock) {
    return () => {
      return this.boondockFacade.disable(boondock);
    };
  }

  async removeSiteOffline(syncId: string) {
    this.subscriptions.push(
      this.offlineBoondockService.remove(syncId).subscribe(() => {
        this.boondockRemoved.emit(syncId);
      }),
    );
  }

  removeBoondock(id?: number | string): void {
    if (id) {
      this.boondockFacade.remove(id);
      this.boondockRemoved.emit(this.boondock.syncId);
    }
  }

  removeBoondockFunction(id?: number | string): () => void {
    if (this.connectionStatus.connected && this.boondock?.id) {
      return () => this.removeBoondock(id);
    } else {
      return () => this.removeSiteOffline(id as string);
    }
  }

  publish(boondock: Boondock) {
    return () => {
      return this.boondockFacade.enable(boondock);
    };
  }

  async resumeWizard() {
    const loading = await this.loadingController.create({
      message: 'Loading Boondock Information...',
      duration: 15000,
      backdropDismiss: true,
    });

    await loading.present();
    if (this.boondock.id) {
      // Load in the complete boondock info. This helps both ensure that the complete record is loaded
      // and all of its parts are up-to-date.
      this.subscriptions.push(
        this.boondockService.fetch(this.boondock.id).subscribe({
          next: async (boondock: IBoondock) => {
            const flow: FlowType = FlowType.boondock;

            const steps = findBoondockSteps();
            const latestStepType = findBoondockLatestStep(flow, boondock, this.boondock.user) || steps[0].type;
            const currentStep = steps.find((el) => el.type === latestStepType);

            if (currentStep) {
              this.signUpFacade.resetProcess();
              this.signUpFacade.setState({
                user: this.user,
                boondock,
                steps,
                currentStep,
                spinner: false,
              });
              await this.router.navigate(currentStep.route);
            } else {
              this.logger.error('Failed to find current step to resume boondock.');
              this.alertFacade.display({
                title: 'Failed to Resume Site',
                body: 'Failed to resume boondock. Could not find where to go!',
                level: 'error',
              });
            }
          },
          error: () => {
            loading.dismiss();
          },
          complete: () => {
            loading.dismiss();
          },
        }),
      );
    }
  }

  makeBoondockViewLink() {
    if (this.boondock.id) {
      this.boondockRoute = HelperFunctions.buildBoondockRoute(this.boondock).join('/');
    }
  }

  updateCompletionStatus(boondock: IBoondock) {
    this.completePercent = boondockCompletePercentage(FlowType.boondock, boondock, boondock?.user);
    this.incompleteMessages = boondockComplete.boondockPercentComplete(boondock).messages;
  }
}
